/**
 * TourAIAdapter
 *
 * Converts tour state (current step, flow, module) into a structured AI context
 * payload that can be consumed by useSemanticContext, the tour-ai-hint API,
 * and the AIAssistantChat inline Q&A.
 */

import type {
  TourFlow,
  TourStep,
  TourAIConfig,
  TourAIStepConfig,
} from '@/config/guided-tours/types';

export interface TourAIContextPayload {
  moduleKey: string;
  moduleName: string;
  flowId: string;
  flowLabel: string;
  flowSummary: string;
  stepId: string;
  stepIndex: number;
  stepTitle: string;
  stepSummary: string;
  stepBullets: string[];
  stepHintContext?: string;
  role: string;
  industrySolution: string | null;
  sandboxActive: boolean;
  aiConfig: TourAIConfig | null;
  stepAIConfig: TourAIStepConfig | null;
  /** Combined context string suitable for Gemini prompt injection */
  contextString: string;
}

interface BuildContextOptions {
  moduleKey: string;
  moduleName: string;
  flow: TourFlow | null;
  step: TourStep | null;
  stepIndex: number;
  role: string;
  industrySolution?: string | null;
  sandboxActive?: boolean;
  aiConfig?: TourAIConfig | null;
}

export function buildTourAIContext(opts: BuildContextOptions): TourAIContextPayload {
  const {
    moduleKey,
    moduleName,
    flow,
    step,
    stepIndex,
    role,
    industrySolution = null,
    sandboxActive = false,
    aiConfig = null,
  } = opts;

  const stepAIConfig = step?.ai ?? null;

  const contextParts: string[] = [
    `Module: ${moduleName} (${moduleKey})`,
    `Role: ${role}`,
  ];

  if (industrySolution) {
    contextParts.push(`Industry: ${industrySolution}`);
  }

  if (sandboxActive) {
    contextParts.push('Mode: Sandbox (demo data)');
  }

  if (flow) {
    contextParts.push(`Flow: ${flow.label} — ${flow.summary}`);
  }

  if (step) {
    contextParts.push(`Step ${stepIndex + 1}: ${step.title}`);
    if (step.summary) {
      contextParts.push(step.summary);
    }
    if (step.bullets.length > 0) {
      contextParts.push(`Key points: ${step.bullets.join('; ')}`);
    }
    if (step.ai?.hint_context) {
      contextParts.push(`Hint context: ${step.ai.hint_context}`);
    }
  }

  return {
    moduleKey,
    moduleName,
    flowId: flow?.id ?? '',
    flowLabel: flow?.label ?? '',
    flowSummary: flow?.summary ?? '',
    stepId: step?.id ?? '',
    stepIndex,
    stepTitle: step?.title ?? '',
    stepSummary: step?.summary ?? '',
    stepBullets: step?.bullets ?? [],
    stepHintContext: step?.ai?.hint_context,
    role,
    industrySolution,
    sandboxActive,
    aiConfig,
    stepAIConfig,
    contextString: contextParts.join('\n'),
  };
}

/**
 * Build the system prompt for the tour-ai-hint API endpoint.
 * Instructs Gemini to generate a concise, context-aware hint.
 */
export function buildHintSystemPrompt(payload: TourAIContextPayload): string {
  return [
    'You are an AI assistant embedded in a guided tour for a business management platform.',
    `The user is currently on the "${payload.stepTitle}" step in the "${payload.flowLabel}" flow.`,
    `Their role is: ${payload.role}.`,
    payload.industrySolution ? `Their industry is: ${payload.industrySolution}.` : '',
    'Generate a concise, helpful tip (max 2 sentences) that is specifically relevant to this step.',
    'If the industry is known, tailor the example to that industry.',
    'Do not repeat the step instructions. Add new, contextual insight.',
    'Do not use markdown formatting. Plain text only.',
  ]
    .filter(Boolean)
    .join('\n');
}

/**
 * Build the system prompt for adaptive bullet generation.
 */
export function buildAdaptiveBulletsPrompt(payload: TourAIContextPayload): string {
  return [
    'You are an AI assistant that rewrites guided tour bullet points to be industry-specific.',
    `The user's role is: ${payload.role}.`,
    payload.industrySolution ? `Their industry is: ${payload.industrySolution}.` : '',
    `Original bullets for "${payload.stepTitle}":`,
    ...payload.stepBullets.map((b, i) => `  ${i + 1}. ${b}`),
    '',
    'Rewrite each bullet to be more relevant to the user\'s industry and role.',
    'Keep the same number of bullets. Keep each bullet under 15 words.',
    'Return as a JSON array of strings. No markdown, no commentary.',
  ]
    .filter(Boolean)
    .join('\n');
}
